import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboardsApi } from '../api/dashboards';
import PremiumLayout from '../components/PremiumLayout';
import {
  RocketLaunchIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const DeliveryDashboard: React.FC = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'delivery'],
    queryFn: dashboardsApi.getDelivery,
  });

  if (isLoading) {
    return (
      <PremiumLayout title="Delivery Dashboard">
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600"></div>
        </div>
      </PremiumLayout>
    );
  }

  if (error) {
    return (
      <PremiumLayout title="Delivery Dashboard">
        <div className="text-center py-12">
          <p className="text-red-600">Error loading dashboard data</p>
        </div>
      </PremiumLayout>
    );
  }

  const dashboardData = data || {} as any;
  const kickoffStatus = dashboardData.kickoffStatus || [];
  const pendingBilling = dashboardData.pendingBilling || [];
  const overdueInvoices = dashboardData.overdueInvoices || [];
  const deliveryRisks = dashboardData.deliveryRisks || [];

  const totalPending = pendingBilling.length;
  const totalOverdue = overdueInvoices.length;
  const totalAtRisk = deliveryRisks.length;
  const totalKickoffs = kickoffStatus.length;

  return (
    <PremiumLayout
      title="Delivery Dashboard"
      subtitle="Kickoff tracking, billing governance, and at-risk deliveries"
    >
      <div className="space-y-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="premium-card p-6 bg-gradient-to-br from-blue-50 to-cyan-50">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-500 rounded-xl shadow-lg">
                <RocketLaunchIcon className="h-7 w-7 text-white" />
              </div>
            </div>
            <p className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">Active Kickoffs</p>
            <p className="text-3xl font-extrabold text-gray-900">{totalKickoffs}</p>
          </div>

          <div className="premium-card p-6 bg-gradient-to-br from-yellow-50 to-orange-50">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-yellow-500 rounded-xl shadow-lg">
                <ClockIcon className="h-7 w-7 text-white" />
              </div>
            </div>
            <p className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">Pending Billing</p>
            <p className="text-3xl font-extrabold text-gray-900">{totalPending}</p>
          </div>

          <div className="premium-card p-6 bg-gradient-to-br from-red-50 to-pink-50">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-red-500 rounded-xl shadow-lg">
                <ExclamationTriangleIcon className="h-7 w-7 text-white" />
              </div>
            </div>
            <p className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">Overdue Invoices</p>
            <p className="text-3xl font-extrabold text-gray-900">{totalOverdue}</p>
          </div>

          <div className="premium-card p-6 bg-gradient-to-br from-purple-50 to-indigo-50">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-500 rounded-xl shadow-lg">
                <CurrencyDollarIcon className="h-7 w-7 text-white" />
              </div>
            </div>
            <p className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">At-Risk Deliveries</p>
            <p className="text-3xl font-extrabold text-gray-900">{totalAtRisk}</p>
          </div>
        </div>

        {/* Overdue Invoices */}
        {overdueInvoices.length > 0 && (
          <div className="premium-card p-6 border-l-4 border-red-500">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mr-2" />
              Overdue Invoices
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Lead</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Milestone</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Due Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Days Overdue</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Owner</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {overdueInvoices.map((invoice: any) => (
                    <tr key={invoice.id} className="hover:bg-red-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {invoice.lead_name || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {invoice.milestone_name || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          {invoice.days_overdue} days
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        ${invoice.amount ? parseFloat(invoice.amount).toLocaleString() : '0'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{invoice.owner_name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pending Billing */}
        <div className="premium-card p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Pending Billing (Next 30 Days)</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Lead</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Milestone</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Due Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Owner</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pendingBilling.map((billing: any) => (
                  <tr key={billing.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {billing.lead_name || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {billing.milestone_name || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {billing.due_date ? new Date(billing.due_date).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        {billing.status || 'pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      ${billing.amount ? parseFloat(billing.amount).toLocaleString() : '0'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{billing.owner_name || '—'}</td>
                  </tr>
                ))}
                {pendingBilling.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      No pending billing items
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Delivery Risks */}
        {deliveryRisks.length > 0 && (
          <div className="premium-card p-6 border-l-4 border-orange-500">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <ExclamationTriangleIcon className="h-6 w-6 text-orange-500 mr-2" />
              At-Risk Deliveries
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Lead</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Stage</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Overdue</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Pending</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Completed</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Owner</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {deliveryRisks.map((risk: any) => (
                    <tr key={risk.id} className="hover:bg-orange-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {risk.lead_name || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {risk.stage || '—'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          {risk.overdue_milestones || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {risk.pending_milestones || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${risk.completed_amount ? parseFloat(risk.completed_amount).toLocaleString() : '0'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{risk.owner_name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </PremiumLayout>
  );
};

export default DeliveryDashboard;
